import { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const MouseTrail = ({ dotCount = 35 }) => {
  const dotsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);
  const { isDarkMode } = useTheme();
  
  const color = isDarkMode 
    ? 'rgba(138, 100, 255, 0.8)' 
    : 'rgba(99, 102, 241, 0.6)';

  useEffect(() => {
    // Create dot elements
    const dots = [];
    
    class Dot {
      constructor() {
        this.x = 0;
        this.y = 0;
        this.node = document.createElement('div');
        this.node.className = 'mouse-trail-dot';
        this.node.style.cssText = `
          position: fixed;
          height: 10px;
          width: 10px;
          border-radius: 5px;
          background: ${color};
          box-shadow: 0 0 12px ${color}, 0 0 24px ${color};
          pointer-events: none;
          z-index: 9999;
          transition: opacity 0.3s ease;
        `;
        document.body.appendChild(this.node);
      }

      draw() {
        this.node.style.left = this.x + 'px';
        this.node.style.top = this.y + 'px';
      }

      updateColor(newColor) {
        this.node.style.background = newColor;
        this.node.style.boxShadow = `0 0 12px ${newColor}, 0 0 24px ${newColor}`;
      }

      destroy() {
        if (this.node && this.node.parentNode) {
          this.node.parentNode.removeChild(this.node);
        }
      }
    }

    // Create dots
    for (let i = 0; i < dotCount; i++) {
      const dot = new Dot();
      // Make dots progressively smaller and more transparent
      const scale = 1 - (i * 0.025);
      const opacity = 1 - (i * 0.025);
      dot.node.style.transform = `scale(${Math.max(scale, 0.15)})`;
      dot.node.style.opacity = Math.max(opacity, 0.08);
      dots.push(dot);
    }

    dotsRef.current = dots;

    // Draw function
    const draw = () => {
      let x = mouseRef.current.x;
      let y = mouseRef.current.y;

      dots.forEach((dot, index) => {
        const nextDot = dots[index + 1] || dots[0];

        dot.x = x;
        dot.y = y;
        dot.draw();

        x += (nextDot.x - dot.x) * 0.6;
        y += (nextDot.y - dot.y) * 0.6;
      });
    };

    // Animation loop
    const animate = () => {
      draw();
      animationRef.current = requestAnimationFrame(animate);
    };

    // Mouse move handler
    const handleMouseMove = (event) => {
      mouseRef.current.x = event.pageX;
      mouseRef.current.y = event.pageY;
    };

    // Start animation and add listener
    window.addEventListener('mousemove', handleMouseMove);
    animate();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      dots.forEach(dot => dot.destroy());
    };
  }, [color, dotCount]);

  return null; // This component doesn't render anything visible itself
};

export default MouseTrail;
